import {
  createShouldForwardProp,
  props,
} from '@styled-system/should-forward-prop';

/**
 * Styled-components prop filtering function that prevents style-related props from being forwarded to the DOM.
 *
 * ## What is this?
 * A shouldForwardProp function created using styled-system's utility that determines which props
 * should be passed through to the underlying DOM element and which should be filtered out.
 *
 * ## Why is this needed?
 * - **Clean DOM**: Prevents style props like `m`, `p`, `color`, `bg` from appearing as HTML attributes
 * - **HTML validation**: Avoids invalid HTML attributes that browsers don't recognize
 * - **Performance**: Reduces DOM noise and improves rendering performance
 * - **styled-system integration**: Works seamlessly with styled-system props
 * - **Developer experience**: Keeps React DevTools clean by hiding internal style props
 *
 * ## Filtered Props:
 * The function filters out common styled-system props including:
 * - Space props: `m`, `margin`, `p`, `padding`, `mt`, `mb`, `ml`, `mr`, etc.
 * - And many more styled-system properties
 *
 * @example
 * ```tsx
 * // Usage with styled-components
 * const Box = styled.div.withConfig({
 *   shouldForwardProp, // Filters out style props
 * })<BoxProps>`
 *   ${space}
 *   ${layout}
 *   ${color}
 * `;
 *
 * // Component usage
 * <Box
 *   m={4}              // ❌ Filtered out - won't appear in DOM
 *   p={2}              // ❌ Filtered out - won't appear in DOM
 *   color="primary"    // ❌ Filtered out - won't appear in DOM
 *   id="my-box"        // ✅ Forwarded - valid HTML attribute
 *   className="custom" // ✅ Forwarded - valid HTML attribute
 *   onClick={handler}  // ✅ Forwarded - valid React prop
 * >
 *   Content
 * </Box>
 *
 * // Resulting DOM (clean HTML)
 * <div id="my-box" class="custom sc-123abc">
 *   Content
 * </div>
 *
 * // Without shouldForwardProp (messy DOM)
 * <div
 *   id="my-box"
 *   class="custom sc-123abc"
 *   m="4"           // ❌ Invalid HTML attribute
 *   p="2"           // ❌ Invalid HTML attribute
 *   color="primary" // ❌ Invalid HTML attribute
 * >
 *   Content
 * </div>
 * ```
 *
 * @see {@link createShouldForwardProp} - styled-system utility for creating prop filters
 * @see {@link props} - Default styled-system props configuration
 */
export const shouldForwardProp = createShouldForwardProp(props);
